import React from 'react';
import { ReportSummary } from '../../../types/timeReport';
import { formatHours } from '../../../utils/timeReportCalculations';

interface TimeReportSummaryProps {
  summary: ReportSummary;
}

interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: string;
  color: 'blue' | 'green' | 'yellow' | 'red';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
  };

  const borderColors = {
    blue: 'border-blue-200',
    green: 'border-green-200',
    yellow: 'border-yellow-200',
    red: 'border-red-200',
  };

  return (
    <div className={`bg-white rounded-lg shadow border-2 ${borderColors[color]} p-6`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
            {title}
          </p>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {value}
          </p>
          {subtitle && (
            <p className="mt-1 text-sm text-gray-500">
              {subtitle}
            </p>
          )}
        </div>
        <div className={`flex-shrink-0 ${colorClasses[color]} p-3 rounded-full text-2xl`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

const TimeReportSummary: React.FC<TimeReportSummaryProps> = ({ summary }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
      <StatCard
        title="Total Hours"
        value={formatHours(summary.total_hours)}
        subtitle={`${summary.total_shifts} total shifts`}
        icon="⏱️"
        color="blue"
      />

      <StatCard
        title="Average Per Day"
        value={formatHours(summary.avg_hours_per_day)}
        subtitle={`${summary.days_worked} days worked`}
        icon="📊"
        color="green"
      />

      <StatCard
        title="Active Shifts"
        value={summary.active_shifts.toString()}
        subtitle="Currently clocked in"
        icon="🟢"
        color="yellow"
      />

      <StatCard
        title="Needs Attention"
        value={summary.incomplete_shifts.toString()}
        subtitle="Incomplete shifts"
        icon="⚠️"
        color="red"
      />
    </div>
  );
};

export default TimeReportSummary;
